import React from 'react';
import { Image, View, Text } from 'react-native';
import { Icon as RNIcon } from 'react-native-elements';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';

import ProductItemProps from 'types/components/ProductItemProps';
import ProductImageTypes from 'types/enums/ProductImageTypes';
import AppContext from 'contexts/AppContext';
import { styles } from 'styles/components/productItem';
import { greenColor } from 'styles/global';
import { productIsEmpty } from 'utils/products';
import GuarnicionesExtrasModal from './GuarnicionesExtrasModal';
import Product from 'types/models/Product';
import { getGuanicionesExtrasProduct } from 'services/productsService';
import ProductType from 'types/enums/ProductType';
import GuarnicionExtraItem from './GuarnicionExtraItem';
import useGuarnicionExtra from 'hooks/useGuarnicionExtra';
import { valueToCurrencyFormat } from 'utils/parseCurrency';
import { productIsValidForCurrentTomorrowUser } from 'utils/products';

export default ({
  product,
  initialQuantity,
  showControlQuantity,
  clothingSize,
  onChangeQuantity,
  onDeleteProduct,
  addToCart,
  actionsEnabled,
  setActionsEnabled,
  tomorrowGlobalRelationQuantityOnShoppingCartItems,
  tomorrowQuantityBenefit,
  productUnits,
}: ProductItemProps): JSX.Element => {
  const { currentVenue, pairedWithClover, currentAuthData } = React.useContext(AppContext);
  const { removeGuarnicionExtraFromMainProduct, onRemoveGuarnicionExtra, onAddGuarnicionExtra } = useGuarnicionExtra({
    onDeleteProduct,
    onChangeQuantity,
  });
  const [productQuantity, setQuantity] = React.useState<number>(initialQuantity ?? 1);
  const [guarnicionesExtras, setGuarnicionesExtras] = React.useState<Product[]>([]);
  const appColor = currentVenue?.appColor ?? greenColor;
  const { t } = useTranslation();

  React.useEffect(() => {
    if (initialQuantity) setQuantity(initialQuantity);
  }, [initialQuantity]);

  const handleFetchGuarnicionesAndExtras = async () => {
    if (product.typeId !== ProductType.WExtra && product.typeId !== ProductType.WGuarnicion) return;
    const items = await getGuanicionesExtrasProduct(product.id);
    setGuarnicionesExtras(items);
    if (items.length === 0 && setActionsEnabled) {
      setActionsEnabled(true);
    }
  };

  function debounce<Func extends (...args: any[]) => void>(func: Func, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<Func>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  const debouncedChangeQuantity = React.useMemo(() => {
    return debounce((productId: string, quantity: number, clothingSize?: number) => {
      if (onChangeQuantity) onChangeQuantity(productId, quantity, clothingSize);
    }, 400);
  }, [onChangeQuantity]);

  const onClickAdd = (clothingSize?: number) => {
    const newQuantity = productQuantity + 1;
    setQuantity(newQuantity);
    debouncedChangeQuantity(product.id, newQuantity, clothingSize);
  };

  const onRemoveProduct = (clothingSize?: number) => {
    const newQuantity = productQuantity - 1;
    setQuantity(newQuantity);
    debouncedChangeQuantity(product.id, newQuantity, clothingSize);
  };

  const onClickDelete = () => {
    // Si el cantidad de unidades es igual a 1 lo que hacemos es eliminar todo el
    // producto del carrito, si es mayor a 1 simplemente restamos 1
    if (productQuantity === 1 && onDeleteProduct) {
      onDeleteProduct(product.id, clothingSize?.id);
    } else if (productQuantity > 1) {
      onRemoveProduct();
    }
    removeGuarnicionExtraFromMainProduct(product.id);
  };

  const showBonusProduct = (product: Product): boolean => {
    if (!currentAuthData || !currentAuthData.user.enabledToTomorrowBenefits || !currentAuthData.user.tomorrowPlanId)
      return false;
    if (!product.isUsedForTomorrowBenefit) return false;
    return productIsValidForCurrentTomorrowUser(
      product.tomorrowGlobalProduct?.tomorrowPlans,
      false,
      currentAuthData.user.tomorrowPlanId,
    );
  };

  const showRemainingBenefitQuantity = (product: Product): number => {
    if (!product.tomorrowGlobalProduct?.amountInStockAvailableForTomorrowBenefit) return 0;
    if (!product.tomorrowGlobalProduct?.tomorrowBenefitDiscountPercentage) return 0;

    // Primero hacemos la resta de la cantidad que tiene disponible el
    // producto para beneficios menos los que tiene en el carrito
    let remaining =
      product.tomorrowGlobalProduct?.amountInStockAvailableForTomorrowBenefit -
      (tomorrowGlobalRelationQuantityOnShoppingCartItems ?? 0);
    // Y luego restamos los productos ya consumidos
    const currentGlobalTomorrowConsumption = currentAuthData?.user.tomorrowConsumptions.find(
      (consumption) => consumption.tomorrowGlobalBenefitProductId === product?.tomorrowGlobalProduct?.id,
    );

    if (currentGlobalTomorrowConsumption) {
      remaining -= currentGlobalTomorrowConsumption.amountConsumed;
    }

    return remaining;
  };

  const renderAddButton = (): JSX.Element => {
    return (
      <View>
        <RNIcon
          name="add-circle-outline"
          color={appColor}
          size={pairedWithClover ? 50 : 22}
          disabled={actionsEnabled === false}
          onPress={async () => {
            if (!addToCart) return;
            if (setActionsEnabled && [ProductType.WExtra, ProductType.WGuarnicion].includes(product.typeId))
              setActionsEnabled(false);
            await handleFetchGuarnicionesAndExtras();
            addToCart(product);
          }}
        />
      </View>
    );
  };

  const renderControlQuantity = () => {
    return (
      <View
        style={[
          styles.controlQuantityContainerNew,
          pairedWithClover && { width: 210, height: 81, borderRadius: 100, alignItems: 'center' },
          { borderColor: appColor },
        ]}
      >
        <RNIcon
          name={productQuantity === 1 ? 'delete-outline' : 'remove'}
          color={appColor}
          size={pairedWithClover ? 50 : 22}
          disabled={actionsEnabled === false}
          onPress={onClickDelete}
        />
        <Text
          style={[
            styles.productQuantityText,
            pairedWithClover && { lineHeight: 56, fontSize: 48, marginTop: 10 },
            { color: appColor },
          ]}
        >
          {productQuantity}
        </Text>
        <RNIcon
          name="add"
          disabled={actionsEnabled === false}
          color={appColor}
          size={pairedWithClover ? 50 : 22}
          onPress={async () => {
            if (setActionsEnabled && [ProductType.WExtra, ProductType.WGuarnicion].includes(product.typeId))
              setActionsEnabled(false);
            await handleFetchGuarnicionesAndExtras();
            onClickAdd(clothingSize?.id);
          }}
        />
      </View>
    );
  };

  const renderQuantityManager = () => {
    if (product.typeId === ProductType.Guarnicion || product.typeId === ProductType.Extra) {
      return (
        <View style={{ display: 'flex', flexDirection: 'row' }}>
          <Text>({initialQuantity})</Text>
          <RNIcon
            name={initialQuantity === 1 ? 'delete-outline' : 'remove-circle-outline'}
            color={appColor}
            size={22}
            onPress={onClickDelete}
          />
        </View>
      );
    }

    if (initialQuantity === 0) return renderAddButton();

    if (initialQuantity !== 0 && showControlQuantity && product.price !== 0) return renderControlQuantity();

    return null;
  };

  return (
    <View
      style={[
        styles.productItem,
        Platform.OS === 'android' && productIsEmpty(product) && { shadowColor: 'rgba(0, 0, 0, 0.05)' },
        pairedWithClover && { width: 979, height: 303, borderRadius: 40, padding: 0 },
      ]}
    >
      <View style={[pairedWithClover ? styles.productImageContainerForTotem : styles.productImageContainer]}>
        <Image
          style={[pairedWithClover ? styles.productImageForTotem : styles.productImageNew]}
          source={{
            uri:
              product.images.find((image) => image.productImageTypeId === ProductImageTypes.logo)?.path ??
              'https://via.placeholder.com/100',
          }}
        />
      </View>
      <View style={[styles.productInfoContainer, pairedWithClover && { width: 585 }]}>
        <View style={[styles.productNameDescription, pairedWithClover && { height: 150, marginLeft: 25, top: 22 }]}>
          {showBonusProduct(product) && (
            <View>
              <Text>{t('bonus-product')}</Text>
              <Text style={styles.remainingItemsText}>
                {showRemainingBenefitQuantity(product) > 0
                  ? t('tomorrow-remaining-items', {
                      amount: showRemainingBenefitQuantity(product),
                      discount: product.tomorrowGlobalProduct?.tomorrowBenefitDiscountPercentage,
                    })
                  : t('no-benefits-of-product')}
              </Text>
            </View>
          )}
          <Text
            style={[
              styles.productName,
              pairedWithClover
                ? { fontWeight: '500', fontSize: 32, lineHeight: 40, letterSpacing: 0.25, color: '#000000' }
                : { color: appColor },
            ]}
            numberOfLines={2}
          >
            {product.name}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {productUnits && !onChangeQuantity ? (
              <Text style={[styles.productQuantityTomorrowText]}>x{productUnits}</Text>
            ) : null}

            {tomorrowQuantityBenefit ? (
              <Text style={[styles.productQuantityTomorrowText, { marginLeft: 5 }]}>
                {`(${t('with-tomorrow-benefit')} x${tomorrowQuantityBenefit})`}
              </Text>
            ) : null}
          </View>
          <Text
            numberOfLines={1}
            style={[
              pairedWithClover
                ? { fontSize: 20, lineHeight: 40, fontWeight: '400', letterSpacing: 0.25, marginTop: 10 }
                : { marginTop: 5 },
            ]}
          >
            {product.description}
          </Text>
          {product.isClothing && clothingSize ? <Text numberOfLines={1}>Talle: {clothingSize.description}</Text> : null}
        </View>
        <View style={styles.priceQuantityContainer}>
          <View style={[styles.priceContainer, pairedWithClover && { marginLeft: 25 }]}>
            <Text
              style={[
                styles.priceText,
                pairedWithClover && { fontSize: 32, lineHeight: 20, letterSpacing: 0.25, fontWeight: '700' },
                { color: currentVenue?.appColor },
              ]}
            >
              {valueToCurrencyFormat(product.price)}
            </Text>
          </View>
          <View style={[styles.addToCartControlQuantity, pairedWithClover && { marginTop: 45, marginRight: 15 }]}>
            {renderQuantityManager()}
          </View>
        </View>

        <GuarnicionExtraItem
          currentVenue={currentVenue}
          productId={product.id}
          onRemoveGuarnicionExtra={onRemoveGuarnicionExtra}
        />

        {(product.typeId === ProductType.WGuarnicion || product.typeId === ProductType.WExtra) && (
          <GuarnicionesExtrasModal
            key={product.id}
            product={product}
            visible={actionsEnabled === false}
            onClose={() => {
              setGuarnicionesExtras([]);
              if (setActionsEnabled) setActionsEnabled(true);
            }}
            onChangeQuantity={onChangeQuantity}
            items={guarnicionesExtras}
            currentVenue={currentVenue}
            onAddGuarnicionExtra={onAddGuarnicionExtra}
          />
        )}
      </View>
    </View>
  );
};
